import React from 'react';
import { Spin } from 'antd';
import './PaymentHistoryModal.css';
import { useModalWidth } from '../hooks/useModalWidth';

function formatCurrency(value) {
  if (!value && value !== 0) return 'GHS 0';
  const num = Number(String(value).replace(/[^\d.]/g, ''));
  if (isNaN(num)) return `GHS ${value}`;
  return `GHS ${num.toLocaleString()}`;
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getStatusBadge(status) {
  const statusConfig = {
    'SUCCESS': { color: '#52c41a', bg: '#f6ffed', text: 'Success' },
    'SUCCESSFUL': { color: '#52c41a', bg: '#f6ffed', text: 'Success' },
    'pending': { color: '#fa8c16', bg: '#fff7e6', text: 'Pending' },
    'failed': { color: '#ff4d4f', bg: '#fff2f0', text: 'Failed' },
    'FAILED': { color: '#ff4d4f', bg: '#fff2f0', text: 'Failed' },
  };

  const config = statusConfig[status] || { color: '#666', bg: '#f5f5f5', text: status };

  return (
    <span style={{
      padding: '4px 12px',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      color: config.color,
      background: config.bg,
      border: `1px solid ${config.color}20`,
    }}>
      {config.text}
    </span>
  );
}

export function PaymentHistoryModal({ 
  isOpen, 
  onClose, 
  payments, 
  isLoading,
  productType
}) {
  const modalWidth = useModalWidth();
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          minWidth: 320,
          maxWidth: Math.max(modalWidth, 800),
          width: '90%',
          boxShadow: '0 8px 40px 0 rgba(0,0,0,0.13)',
          padding: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '80vh',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 32px 20px 32px' }}>
          <div className="modal-title" style={{ 
            fontWeight: 800, 
            color: '#222', 
            marginBottom: 6, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between' 
          }}>
            <span>{productType} Payment History</span>
            <button 
              onClick={onClose} 
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: 22, 
                color: '#888', 
                cursor: 'pointer', 
                marginLeft: 12, 
                fontWeight: 700, 
                lineHeight: 1 
              }} 
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p style={{ color: '#888', fontSize: 15, marginBottom: 0, marginTop: 0 }}>
            View your past {productType.toLowerCase()} payments and their status
          </p>
        </div>

        {/* Content */}
        <div style={{ 
          padding: '0 32px 32px 32px', 
          overflowY: 'auto', 
          flex: 1,
          maxHeight: 'calc(80vh - 120px)'
        }}>
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#666' }}>Loading payment history...</div>
            </div>
          ) : !payments || payments.length === 0 ? (
            <div style={{ 
              padding: 40, 
              textAlign: 'center', 
              color: '#666',
              background: '#f8f9fa',
              borderRadius: 12,
              border: '1px dashed #ddd'
            }}>
              <div className="empty-state-message" style={{ marginBottom: 8 }}>No payment history found</div>
              <div style={{ fontSize: 14 }}>Your {productType.toLowerCase()} payment history will appear here</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {payments
                .sort((a, b) => {
                  // Sort by created_at in descending order (newest first)
                  const dateA = new Date(a.created_at);
                  const dateB = new Date(b.created_at);
                  return dateB - dateA; // Descending order
                })
                .map((payment, index) => (
                <div
                  key={payment.id || index}
                  style={{
                    background: '#f8f9fa',
                    borderRadius: 12,
                    padding: '20px',
                    border: '1px solid #eee',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  {/* Header Row */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    gap: 16
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 700, 
                        fontSize: 16,
                         
                        color: '#333', 
                        marginBottom: 4 
                      }}>
                        {payment.product || 'Payment'}
                      </div>
                      {payment.policy_number && (
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          Policy: {payment.policy_number}
                        </div>
                      )}
                      {payment.description && (
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          {payment.description}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                        {formatDate(payment.created_at)}
                      </div>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'flex-end',
                      gap: 8
                    }}>
                      <div style={{ 
                        fontWeight: 700, 
                        fontSize: 18,
                         
                        color: '#333' 
                      }}>
                        {formatCurrency(payment.amount)}
                      </div>
                      {getStatusBadge(payment.status)}
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 24, 
                    fontSize: 12, 
                    color: '#666',
                    borderTop: '1px solid #eee',
                    paddingTop: 12
                  }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>Ref:</span> {payment.client_reference}
                    </div>
                    {payment.payment_reference && (
                      <div className="mobile-hidden">
                        <span style={{ fontWeight: 600 }}>Payment ID:</span> {payment.payment_reference}
                      </div>
                    )}
                    {payment.payment_channel && (
                      <div className="mobile-hidden">
                        <span style={{ fontWeight: 600 }}>Channel:</span> {payment.payment_channel}
                      </div>
                    )}
                    {payment.completed_at && (
                      <div className="mobile-hidden">
                        <span style={{ fontWeight: 600 }}>Completed:</span> {formatDate(payment.completed_at)}
                      </div>
                    )}
                  </div>

                  {/* Failure Reason */}
                  {payment.failure_reason && (
                    <div style={{
                      padding: 8,
                      background: '#fff2f0',
                      border: '1px solid #ffccc7',
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#ff4d4f',
                    }}>
                      <span style={{ fontWeight: 600 }}>Failure Reason:</span> {payment.failure_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
